import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type {
  Framework,
  Control,
  ControlImplementation,
  EvidenceItem,
  ComplianceException,
  ComplianceReadiness,
  ControlFilters,
  EvidenceFilters,
  ExceptionFilters,
  ControlPostureResult,
} from '@/api/types/compliance.types';

// ── Readiness ─────────────────────────────────────────────────────────────────

export function useReadiness() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['compliance-readiness', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/readiness');
      return data.data as ComplianceReadiness[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useFrameworkReadiness(frameworkId: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['framework-readiness', tenantId, frameworkId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/readiness/${frameworkId}`);
      return data.data as { readiness: ComplianceReadiness; controls_by_category: Record<string, Control[]> };
    },
    enabled: !!tenantId && !!frameworkId,
    staleTime: 5 * 60_000,
  });
}

// ── Frameworks + Controls ─────────────────────────────────────────────────────

export function useFrameworks() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['frameworks', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/frameworks');
      return data.data as Framework[];
    },
    enabled: !!tenantId,
    staleTime: 10 * 60_000,
  });
}

export function useActivateFramework() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (frameworkId: string) => {
      const { data } = await apiClient.post(`/compliance/frameworks/${frameworkId}/activate`);
      return data.data as { activated: boolean; controls_added: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-readiness', tenantId] });
      qc.invalidateQueries({ queryKey: ['framework-readiness', tenantId] });
      qc.invalidateQueries({ queryKey: ['frameworks', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'compliance', tenantId] });
    },
  });
}

export function useDeactivateFramework() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (frameworkId: string) => {
      const { data } = await apiClient.delete(`/compliance/frameworks/${frameworkId}/activate`);
      return data.data as { deactivated: boolean };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-readiness', tenantId] });
      qc.invalidateQueries({ queryKey: ['framework-readiness', tenantId] });
      qc.invalidateQueries({ queryKey: ['frameworks', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'compliance', tenantId] });
    },
  });
}

export function useFrameworkControls(frameworkId: string, filters?: ControlFilters, page = 0, perPage = 50) {
  const { tenantId } = useAuthStore();
  const params = new URLSearchParams({ limit: String(perPage), offset: String(page * perPage) });
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);

  return useQuery({
    queryKey: ['framework-controls', tenantId, frameworkId, filters, page],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/frameworks/${frameworkId}/controls?${params}`);
      const result = data as { data: Control[]; meta: { total: number } };
      // Normalise control IDs at the source — strips erroneous spaces after '.'
      // (e.g. 'PR. DS-01' → 'PR.DS-01') that may exist in the DB.
      result.data = result.data.map((c) => ({
        ...c,
        control_id: c.control_id.replace(/\.\s+/g, '.'),
      }));
      return result;
    },
    enabled: !!tenantId && !!frameworkId,
    staleTime: 0,
  });
}

export function useControl(controlId: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['control', tenantId, controlId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/controls/${controlId}`);
      const result = data.data as Control & { cross_framework_mappings: Array<{ control_id: string; title: string; framework_name: string }> };
      result.control_id = result.control_id.replace(/\.\s+/g, '.');
      if (result.cross_framework_mappings) {
        result.cross_framework_mappings = result.cross_framework_mappings.map((m) => ({
          ...m,
          control_id: m.control_id.replace(/\.\s+/g, '.'),
        }));
      }
      return result;
    },
    enabled: !!tenantId && !!controlId,
    staleTime: 5 * 60_000,
  });
}

export function useControlImplementation(controlId: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['control-impl', tenantId, controlId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/controls/${controlId}/implementation`);
      return data.data as ControlImplementation | null;
    },
    enabled: !!tenantId && !!controlId,
    staleTime: 3 * 60_000,
  });
}

export function useUpdateControlImpl(controlId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      implementationNotes?: string;
      compensatingControlDesc?: string;
      linkedPolicyId?: string | null;
      ownerId?: string | null;
      targetDate?: string | null;
    }) => {
      const { data } = await apiClient.patch(`/compliance/controls/${controlId}/implementation`, body);
      return data.data as ControlImplementation;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['control-impl', tenantId, controlId] });
      qc.invalidateQueries({ queryKey: ['compliance-readiness', tenantId] });
      qc.invalidateQueries({ queryKey: ['framework-controls', tenantId] });
      qc.invalidateQueries({ queryKey: ['framework-readiness', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'compliance', tenantId] });
    },
  });
}

// ── Evidence ──────────────────────────────────────────────────────────────────

export function useControlEvidence(controlId: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['control-evidence', tenantId, controlId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/controls/${controlId}/evidence`);
      return data.data as EvidenceItem[];
    },
    enabled: !!tenantId && !!controlId,
    staleTime: 3 * 60_000,
  });
}

export function useUploadEvidence(controlId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title: string; evidenceType: string; validUntil?: string; sourceModule?: string; sourceRecordId?: string; contentSummary?: string }) => {
      const { data } = await apiClient.post(`/compliance/controls/${controlId}/evidence`, body);
      return data.data as EvidenceItem;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['control-evidence', tenantId, controlId] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'compliance', tenantId] });
    },
  });
}

export function useDeleteEvidence(controlId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (evidenceId: string) => {
      await apiClient.delete(`/compliance/controls/${controlId}/evidence/${evidenceId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['control-evidence', tenantId, controlId] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'compliance', tenantId] });
    },
  });
}

export function useEvidence(filters?: EvidenceFilters, page = 0, perPage = 50) {
  const { tenantId } = useAuthStore();
  const params = new URLSearchParams({ limit: String(perPage), offset: String(page * perPage) });
  if (filters?.type) params.set('type', filters.type);
  if (filters?.source) params.set('source', filters.source);
  if (filters?.expiring) params.set('expiring', String(filters.expiring));
  if (filters?.controlId) params.set('controlId', filters.controlId);
  if (filters?.sourceModule) params.set('sourceModule', filters.sourceModule);

  return useQuery({
    queryKey: ['evidence', tenantId, filters, page],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/evidence?${params}`);
      return data as { data: EvidenceItem[]; meta: { total: number } };
    },
    enabled: !!tenantId,
    staleTime: 3 * 60_000,
  });
}

export function useCollectEvidence() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { sourceModules: string[]; controlId?: string }) => {
      const { data } = await apiClient.post('/compliance/evidence/collect', body);
      return data.data as { collected: number; skipped: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['evidence', tenantId] });
    },
  });
}

// ── Exceptions ────────────────────────────────────────────────────────────────

export function useExceptions(filters?: ExceptionFilters, page = 0, perPage = 20) {
  const { tenantId } = useAuthStore();
  const params = new URLSearchParams({ limit: String(perPage), offset: String(page * perPage) });
  if (filters?.status) params.set('status', filters.status);
  if (filters?.policy_id) params.set('policy_id', filters.policy_id);
  if (filters?.expiring_in_days) params.set('expiring_in_days', String(filters.expiring_in_days));

  return useQuery({
    queryKey: ['exceptions', tenantId, filters, page],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/exceptions?${params}`);
      return data as { data: ComplianceException[]; meta: { total: number; limit: number; offset: number; expiring_soon_count: number } };
    },
    enabled: !!tenantId,
    staleTime: 3 * 60_000,
  });
}

export function useCreateException() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      title: string;
      policyId?: string;
      controlImplementationId?: string;
      businessJustification: string;
      riskDescription: string;
      compensatingControls?: string;
      exceptionStartDate?: string;
      exceptionExpiryDate?: string;
    }) => {
      const { data } = await apiClient.post('/compliance/exceptions', body);
      return data.data as ComplianceException;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exceptions', tenantId] }),
  });
}

export function useApproveException() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.patch(`/compliance/exceptions/${id}/approve`);
      return data.data as ComplianceException;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exceptions', tenantId] }),
  });
}

export function useRejectException() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, rejectionReason }: { id: string; rejectionReason: string }) => {
      const { data } = await apiClient.patch(`/compliance/exceptions/${id}/reject`, { rejectionReason });
      return data.data as ComplianceException;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exceptions', tenantId] }),
  });
}

// ── Audit Package ─────────────────────────────────────────────────────────────

export function useGenerateAuditPackage() {
  return useMutation({
    mutationFn: async (body: { frameworkId: string; auditPeriodStart: string; auditPeriodEnd: string }) => {
      const { data } = await apiClient.post('/compliance/audit-package', body);
      return data.data as { jobId: string; message: string };
    },
  });
}

export function useAuditPackageStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['audit-package-status', jobId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/audit-package/${jobId}`);
      return data.data as { status: 'processing' | 'complete' | 'failed'; downloadUrl?: string };
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'processing' ? 3000 : false;
    },
  });
}

export async function downloadAuditPackage(jobId: string, frameworkName: string): Promise<void> {
  const response = await apiClient.get(`/compliance/audit-package/${jobId}/download`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `audit-package-${frameworkName}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ── Control Posture (CC Increment 1 S4) ───────────────────────────────────────

/**
 * The tenant's canonical control posture. Read-only, tenant-scoped by RLS at the API.
 * ⚠ An empty result is a truthful "nothing assessed yet", not an error and not an all-clear.
 */
export function useControlPosture() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['control-posture', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/control-posture');
      return data.data as ControlPostureResult;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}
